import Section from '@/components/common/Section';
// ...existing code...
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/animations/variants';
import emailjs from '@emailjs/browser';
import { useState, useRef } from 'react';

const insights = [
  {
    title: "Modern Delivery Models for Enterprise Software",
    description: "Exploring how residential engineering teams and consultant networks are reshaping software development for enterprises.",
    date: "2026-01-01",
    readTime: "8 min read",
    category: "Delivery Models",
    image: "/images/blog-future-consultancy.png",
    tags: ["Delivery", "Enterprise", "Teams"]
  },
  {
    title: "Engineering Practices That Scale",
    description: "Best practices for maintaining code quality and team productivity as your engineering organization grows.",
    date: "2025-12-28",
    readTime: "6 min read",
    category: "Engineering",
    image: "/images/blog-engineering-practices.png",
    tags: ["Engineering", "Scaling", "Quality"]
  },
  {
    title: "Scaling Teams and Platforms Simultaneously",
    description: "Strategic approaches to scaling both your technical platform and engineering teams without compromising quality.",
    date: "2025-12-25",
    readTime: "10 min read",
    category: "Scaling",
    image: "/images/blog-scaling-teams.png",
    tags: ["Scaling", "Platform", "Growth"]
  },
  {
    title: "AI-Augmented Development: Beyond GitHub Copilot",
    description: "How AI tools are transforming software development workflows and what it means for engineering productivity.",
    date: "2025-12-20",
    readTime: "7 min read",
    category: "AI Development",
    image: "/images/blog-ai-development.png",
    tags: ["AI", "Development", "Productivity"]
  },
  {
    title: "Building Resilient Enterprise Systems",
    description: "Architecture patterns and practices for building systems that can handle enterprise-scale demands.",
    date: "2025-12-15",
    readTime: "12 min read",
    category: "Architecture",
    image: "/images/blog-enterprise-systems.png",
    tags: ["Architecture", "Enterprise", "Resilience"]
  },
  {
    title: "The Future of Software Consultancy",
    description: "How the consulting model is evolving with remote work, AI tools, and changing client expectations.",
    date: "2025-12-10",
    readTime: "9 min read",
    category: "Industry",
    image: "/images/blog-future-consultancy.png",
    tags: ["Consulting", "Future", "Industry"]
  },
];

const categories = [
  { name: "All", count: 6 },
  { name: "Engineering", count: 2 },
  { name: "Delivery Models", count: 1 },
  { name: "Scaling", count: 1 },
  { name: "AI Development", count: 1 },
  { name: "Architecture", count: 1 },
];

export default function InsightsPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      // Initialize EmailJS with your public key
      emailjs.init('66w6XR1E104wMeNAH');
      
      // Send email using EmailJS
      const response = await emailjs.send(
        'service_wx5sdgl',
        'template_ikojhzu',
        {
          email: email,
          message: `This person has subscribed: ${email}`,
          reply_to: email
        }
      );
      
      console.log('EmailJS response:', response);
      setMessage('✅ Successfully subscribed! Thank you for joining our newsletter.');
      setEmail('');
      
      // Reset form after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('EmailJS subscription error:', error);
      console.error('Error details:', {
        status: error.status,
        message: error.message || error.text,
        error: error
      });
      
      // Try fallback method - save to local storage as backup
      try {
        const subscriptions = JSON.parse(localStorage.getItem('pending_subscriptions') || '[]');
        subscriptions.push({
          email: email,
          timestamp: new Date().toISOString(),
          status: 'pending'
        });
        localStorage.setItem('pending_subscriptions', JSON.stringify(subscriptions));
        
        setMessage('✅ Email saved! We will process your subscription shortly.');
        setEmail('');
        setTimeout(() => setMessage(''), 3000);
      } catch (fallbackError) {
        // More specific error messages based on status
        let errorMessage = '❌ Something went wrong. Please try again later.';
        
        if (error.status === 412) {
          errorMessage = '❌ EmailJS service temporarily unavailable. Please try again later.';
          console.error('EmailJS 412 Error: This usually indicates account limits exceeded or configuration issues');
        } else if (error.status === 400) {
          errorMessage = '❌ Invalid email address. Please check and try again.';
        } else if (error.status === 401) {
          errorMessage = '❌ Email service authentication failed. Please contact support.';
        }
        
        setMessage(errorMessage);
        
        // Clear error message after 5 seconds
        setTimeout(() => setMessage(''), 5000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Insights Overview */}
      <Section
        title="Insights & Thought Leadership"
        subtitle="Sharing our experience and perspectives on software development and technology"
        centered
        background="gradient"
      >
        <motion.p 
          className="text-lg text-textSecondary max-w-4xl mx-auto text-center"
          variants={fadeUp}
        >
          Stay updated with our latest thinking on software engineering, delivery models, 
          team scaling, and technology trends. Our insights are based on real-world experience 
          working with diverse clients across multiple industries.
        </motion.p>
      </Section>

      {/* Category Filter */}
      <Section background="surface">
        <motion.div 
          className="flex flex-wrap justify-center gap-4 mb-12"
          variants={staggerContainer}
        >
          {categories.map((category, index) => (
            <motion.button
              key={index}
              className={`
                px-6 py-3 rounded-full text-sm font-medium transition-all duration-normal
                ${index === 0 
                  ? 'bg-primary text-white shadow-glow' 
                  : 'bg-background text-textSecondary hover:bg-surface hover:text-textPrimary border border-border'
                }
              `}
              variants={fadeUp}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.name} ({category.count})
            </motion.button>
          ))}
        </motion.div>

        {/* Featured Article */}
        <motion.div 
          className="bg-background border border-border rounded-xl overflow-hidden mb-12"
          variants={fadeUp}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="h-64 lg:h-auto bg-gradient-to-br from-primary/20 to-secondary/20 relative">
              <img 
                src={insights[0].image ?? undefined} 
                alt={insights[0].title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.parentElement!.classList.add('flex', 'items-center', 'justify-center');
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = `
                    <div class="text-center text-textSecondary">
                      <div class="text-6xl mb-4">📖</div>
                      <div class="text-lg">Featured Article</div>
                    </div>
                  `;
                }}
              />
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-primary text-white text-xs font-medium rounded-full">
                  Featured
                </span>
              </div>
            </div>
            
            <div className="p-8 lg:p-12">
              <div className="flex items-center text-sm text-textSecondary mb-4">
                <span className="bg-primary/20 text-primary px-3 py-1 rounded-full mr-4">
                  {insights[0].category}
                </span>
                <span>{insights[0].date}</span>
                <span className="mx-2">•</span>
                <span>{insights[0].readTime}</span>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-textPrimary mb-4">
                {insights[0].title}
              </h2>
              
              <p className="text-textSecondary mb-6 leading-relaxed">
                {insights[0].description}
              </p>
              
              <div className="flex flex-wrap gap-2 mb-6">
                {insights[0].tags.map((tag, tagIndex) => (
                  <span 
                    key={tagIndex}
                    className="text-xs px-3 py-1 bg-surface text-textSecondary rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              <button className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors">
                Read Article
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Articles Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={staggerContainer}
        >
          {insights.slice(1).map((insight, index) => (
            <motion.article
              key={index}
              className="bg-background border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-normal group"
              variants={fadeUp}
              whileHover={{ y: -5, boxShadow: "0px 20px 40px rgba(0,0,0,0.15)" }}
            >
              <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 relative overflow-hidden">
                <img 
                  src={insight.image ?? undefined} 
                  alt={insight.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-slow"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.parentElement!.classList.add('flex', 'items-center', 'justify-center');
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `
                      <div class="text-center text-textSecondary">
                        <div class="text-4xl mb-2">📄</div>
                        <div class="text-sm">Article</div>
                      </div>
                    `;
                  }}
                />
              </div>
              
              <div className="p-6">
                <div className="flex items-center text-xs text-textSecondary mb-3">
                  <span className="bg-primary/20 text-primary px-2 py-1 rounded mr-3">
                    {insight.category}
                  </span>
                  <span>{insight.date}</span>
                  <span className="mx-2">•</span>
                  <span>{insight.readTime}</span>
                </div>
                
                <h3 className="text-lg font-heading font-semibold text-textPrimary mb-3 group-hover:text-primary transition-colors">
                  {insight.title}
                </h3>
                
                <p className="text-textSecondary text-sm mb-4 leading-relaxed">
                  {insight.description}
                </p>
                
                <div className="flex flex-wrap gap-1 mb-4">
                  {insight.tags.map((tag, tagIndex) => (
                    <span 
                      key={tagIndex}
                      className="text-xs px-2 py-1 bg-surface text-textSecondary rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                <button className="inline-flex items-center text-primary hover:text-primary/80 font-medium text-sm transition-colors">
                  Read More
                  <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </Section>

      {/* Newsletter Signup */}
      <Section centered>
        <motion.div 
          className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-8 md:p-12 text-center"
          variants={fadeUp}
        >
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-textPrimary mb-4">
            Stay Updated
          </h2>
          <p className="text-lg text-textPrimary mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter and get the latest insights, best practices, 
            and industry trends delivered to your inbox.
          </p>
          <form 
            ref={formRef}
            onSubmit={handleNewsletterSubmit} 
            className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto"
          >
            <input 
              type="email" 
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-textPrimary placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              required
              disabled={isSubmitting}
            />
            <button 
              type="submit"
              disabled={isSubmitting || !email.trim()}
               className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-lg font-heading font-semibold transition-all duration-normal">            
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
          {message && (
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-4 text-sm font-medium text-center px-4 py-2 rounded-lg ${
                message.includes('✅') 
                  ? 'text-green-700 bg-green-50 border border-green-200' 
                  : 'text-red-700 bg-red-50 border border-red-200'
              }`}
            >
              {message}
            </motion.p>
          )}
        </motion.div>
      </Section>

      {/* Call to Action */}
      <Section centered background="surface">
        <motion.div 
          className="text-center"
          variants={fadeUp}
        >
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-textPrimary mb-6">
            Have a Topic Suggestion?
          </h2>
          <p className="text-lg text-textSecondary mb-8 max-w-2xl mx-auto">
            We love hearing from our community. If you have questions or topics you'd like 
            us to explore, don't hesitate to reach out.
          </p>
          <button className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-glow transition-all duration-normal">
            Suggest a Topic
          </button>
        </motion.div>
      </Section>
    </>
  );
}